import { GameRoundStatusId } from '@/models/round/status/id';
import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  ReferenceDot,
  ResponsiveContainer,
  YAxis,
} from 'recharts';
import { useTheme } from 'styled-components';
import { CrashAreaChartProps } from './props';
import styles from './styles.module.scss';

export function CrashAreaChart({
  data,
  status,
  customTicks,
}: CrashAreaChartProps) {
  const theme = useTheme();
  const chartColor =
    status?.id?.toString() === GameRoundStatusId.Completed?.toString()
      ? theme.vars.palette.text.secondary
      : theme.vars.palette.primary.main;
  const isQueued =
    status?.id?.toString() === GameRoundStatusId.Pending?.toString();
  const maxLine = useMemo(
    () => Math.max(...data.map(item => item.line)),
    [data],
  );
  const progress = useMemo(() => {
    return Math.min(maxLine * 20, 100);
  }, [maxLine]);

  return (
    <div className={styles.container}>
      <ResponsiveContainer
        width={`${progress}%`}
        height={`${progress}%`}
        className={styles.container__chart}
      >
        <AreaChart
          data={data}
          margin={{ bottom: 16 }}
          width={progress}
          height={progress}
          syncId="crash"
          {...{
            overflow: 'visible',
          }}
        >
          <defs>
            <linearGradient id="colorChart" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={chartColor} stopOpacity={0.8} />
              <stop offset="100%" stopColor={chartColor} stopOpacity={0} />
            </linearGradient>
          </defs>

          <Area
            type="monotone"
            dataKey="line"
            stroke={chartColor}
            strokeWidth={isQueued ? 0 : 5}
            fillOpacity={isQueued ? 0 : 0.8}
            fill="url(#colorChart)"
            isAnimationActive={false}
          />

          {!isQueued && (
            <ReferenceDot
              x={data.length - 1}
              y={data[data.length - 1]?.line}
              r={8}
              fill={chartColor}
              strokeWidth={0}
              overflow="visible"
            />
          )}

          <YAxis
            domain={[0, maxLine]}
            dataKey="line"
            width={20}
            height={progress}
            tick={{
              stroke: theme.vars.palette.text.secondary,
              strokeWidth: 0,
              fontSize: '12px',
              fontWeight: '500',
            }}
            ticks={customTicks}
            tickCount={3}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
